/*----------------------------------
- PUBLIC PAGE UNAVAILABLE (503)
----------------------------------*/

/**
 * @rule One answer for a public page that cannot show its data right now: 503, `Retry-After`,
 *       never stored. Every page family (the `/domains` listings, the TLD insight pages, the
 *       news archive, the agenda, the applications list) and every mirror that answers a 503
 *       reads its class, its headers and its delay here.
 */

import CoreError from '@common/errors';

/*----------------------------------
- TYPES
----------------------------------*/

type UnavailableRouteRequest = {
    res?: {
        setHeader: (name: string, value: string) => void;
    };
};

type UnavailableRouteResponse = {
    setHeaders?: (headers: Record<string, string>) => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Seconds a crawler is asked to wait before retrying a 503 public page or mirror. */
export const PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS = '300';

/**
 * The header pair of the SSR pages that read Product on every request (the news archive and its
 * editions, the agenda, the applications list): revalidate on every hit, at the browser and at
 * any edge, so a new edition, a filter change or an import shows at once.
 */
export const PUBLIC_REVALIDATE_HEADERS = {
    'Cache-Control': 'public, max-age=0, must-revalidate',
    'Surrogate-Control': 'max-age=0, must-revalidate',
} as const;

/** The same pages when their Product read failed: stored nowhere, retried after the delay above. */
export const PUBLIC_UNAVAILABLE_HEADERS = {
    'Cache-Control': 'no-store',
    'Retry-After': PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS,
    'Surrogate-Control': 'no-store',
} as const;

/*----------------------------------
- ERROR
----------------------------------*/

/**
 * A public page whose data exists but is not ready to show: a `/domains` listing whose cached
 * search is not renderable yet, a TLD insight page before its first nightly rollup, or a page
 * whose Product read failed. Answered 503 with `Retry-After`, so a crawler comes back instead of
 * indexing an empty page, and the static warmup never caches it. Each page passes its own
 * message; the default is the listings' own, which throw it bare.
 */
export class PublicPageUnavailable extends CoreError {
    public http = 503;
    public title = 'Temporarily unavailable';
    public static msgDefaut = 'This public inventory is temporarily unavailable. Please try again shortly.';
}

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * Sets `PUBLIC_UNAVAILABLE_HEADERS` before a page throws `PublicPageUnavailable`: through the
 * render response and on the raw response too, which the error page is written to.
 */
export const setPublicUnavailableHeaders = (request: UnavailableRouteRequest, response?: UnavailableRouteResponse): void => {
    response?.setHeaders?.(PUBLIC_UNAVAILABLE_HEADERS);
    Object.entries(PUBLIC_UNAVAILABLE_HEADERS).forEach(([name, value]) => request.res?.setHeader(name, value));
};
